import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { PageHero } from "@/components/ui/PageHero";
import {
	useCancelOrder,
	useDualMarks,
	useMmkBookDepth,
	useMmkBookStatus,
	useMmkBookTrades,
	useMyMmkOrders,
	usePlaceLimit,
} from "@/hooks/useMmkBook";
import { useAuth } from "@/lib/auth";
import { docsArticlePath } from "@/lib/docs/parse";
import { getMmkBookCanisterId } from "@/lib/mmk-book-actor";
import { getEnvMap } from "@/lib/network";
import { ROUTES } from "@/lib/routes";
import { formatAmountPlain, parseAmount } from "@/lib/utils";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function DepthRow({
	price,
	size,
}: {
	price: number;
	size: number;
}) {
	return (
		<li className="flex items-baseline gap-3 font-mono text-xs tabular-nums">
			<span className="shrink-0">
				{formatAmountPlain(price, 4)}{" "}
				<span className="text-[10px] text-muted-foreground">USDM</span>
			</span>
			<span className="shrink-0 text-muted-foreground">
				{formatAmountPlain(size, 4)}{" "}
				<span className="text-[10px]">MMK</span>
			</span>
		</li>
	);
}

export default function MmkBookPage() {
	const { identity, balances } = useAuth();
	const statusQ = useMmkBookStatus();
	const depthQ = useMmkBookDepth(12);
	const marksQ = useDualMarks();
	const myOrdersQ = useMyMmkOrders(identity);
	const placeMut = usePlaceLimit(identity);
	const cancelMut = useCancelOrder(identity);
	const tradesQ = useMmkBookTrades(15);

	const [side, setSide] = useState<"buy" | "sell">("buy");
	const [price, setPrice] = useState("2.00");
	const [size, setSize] = useState("1");

	const bookId = getMmkBookCanisterId(getEnvMap());
	const st = statusQ.data;
	const marks = marksQ.data;
	const depth = depthQ.data;
	const docsHow = docsArticlePath("governance", "mmk-order-book");

	const estNotional = useMemo(() => {
		const p = parseAmount(price);
		const s = parseAmount(size);
		if (!Number.isFinite(p) || !Number.isFinite(s) || p <= 0 || s <= 0) return null;
		return p * s;
	}, [price, size]);

	async function onPlace() {
		const p = parseAmount(price);
		const s = parseAmount(size);
		if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(s) || s <= 0) {
			toast.error("Enter valid price and size");
			return;
		}
		try {
			const id = await placeMut.mutateAsync({ side, price: p, size: s });
			toast.success(`Order #${id} placed`);
		} catch (e) {
			toast.error(e instanceof Error ? e.message : "Place failed");
		}
	}

	return (
		<div className="pb-16">
			<PageHero
				eyebrow="Secondary market"
				title="MMK Book"
				subtitle="Limit order book for MMK/USDM. Crossing the book fills automatically; same price uses FIFO (first registered first)."
			/>

			<div className="mx-4 mb-4 flex flex-wrap items-center gap-3 text-sm">
				<Link
					to={docsHow}
					className="font-semibold text-primary underline underline-offset-2"
				>
					How the order book works
				</Link>
				<span className="text-xs text-muted-foreground">
					Buy at a price above a resting ask (or sell below a bid) → instant match.
				</span>
			</div>

			{!bookId && (
				<div className="mx-4 mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
					MMK book not wired in this SPA build. Deploy{" "}
					<code className="text-xs">mmk_book</code> and refresh env.
				</div>
			)}

			{/* Dual marks */}
			<section className="mx-4 mb-6 grid gap-3 sm:grid-cols-3">
				<div className="rounded-xl border border-border/60 bg-card/40 p-4">
					<p className="text-xs text-muted-foreground">Primary (only-up)</p>
					<p className="mt-1 text-2xl font-semibold tabular-nums">
						{marks?.primaryPrice != null
							? formatAmountPlain(marks.primaryPrice, 4)
							: "—"}{" "}
						<span className="text-sm font-normal text-muted-foreground">
							USDM/MMK
						</span>
					</p>
					<p className="mt-1 text-xs text-muted-foreground">
						Sold{" "}
						{marks ? `${(marks.soldBps / 100).toFixed(1)}% of cap` : "—"}
					</p>
				</div>
				<div className="rounded-xl border border-border/60 bg-card/40 p-4">
					<p className="text-xs text-muted-foreground">Book mid / last</p>
					<p className="mt-1 text-2xl font-semibold tabular-nums">
						{marks?.secondaryMid != null
							? formatAmountPlain(marks.secondaryMid, 4)
							: marks?.secondaryLast != null
								? formatAmountPlain(marks.secondaryLast, 4)
								: "—"}{" "}
						<span className="text-sm font-normal text-muted-foreground">
							USDM/MMK
						</span>
					</p>
					<p className="mt-1 text-xs text-muted-foreground">Secondary mark</p>
				</div>
				<div className="rounded-xl border border-border/60 bg-card/40 p-4">
					<p className="text-xs text-muted-foreground">Book status</p>
					<p className="mt-1 text-lg font-medium">
						{st?.source === "missing"
							? "Unwired"
							: st?.active
								? "Active"
								: st?.enabled
									? "Waiting activation"
									: "Disabled"}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{st && st.source === "live"
							? `${st.openOrders} open · ${st.tradeCount} trades · fees ${st.makerFeeBps}/${st.takerFeeBps} bps${st.settlementMode ? " · escrow on" : ""}`
							: "—"}
					</p>
				</div>
			</section>

			<div className="mx-4 grid gap-6 lg:grid-cols-2">
				{/* Depth */}
				<section className="rounded-xl border border-border/60 bg-card/30 p-4">
					<div className="mb-2 flex flex-wrap items-end justify-between gap-2">
						<h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
							Depth
						</h2>
						<p className="text-[10px] text-muted-foreground">
							Same price · FIFO (first order id wins)
						</p>
					</div>
					<div className="mb-2 grid grid-cols-2 gap-4 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
						<span>Price · Size</span>
						<span>Price · Size</span>
					</div>
					<div className="grid grid-cols-2 gap-4 text-sm">
						<div>
							<p className="mb-2 text-emerald-400/90">Bids (buy MMK)</p>
							<ul className="space-y-1.5">
								{(depth?.bids ?? []).slice(0, 10).map((r, i) => (
									<DepthRow key={`b-${i}`} price={r.price} size={r.size} />
								))}
								{(!depth?.bids || depth.bids.length === 0) && (
									<li className="text-xs text-muted-foreground">No bids</li>
								)}
							</ul>
						</div>
						<div>
							<p className="mb-2 text-rose-400/90">Asks (sell MMK)</p>
							<ul className="space-y-1.5">
								{(depth?.asks ?? []).slice(0, 10).map((r, i) => (
									<DepthRow key={`a-${i}`} price={r.price} size={r.size} />
								))}
								{(!depth?.asks || depth.asks.length === 0) && (
									<li className="text-xs text-muted-foreground">No asks</li>
								)}
							</ul>
						</div>
					</div>
				</section>

				{/* Place form */}
				<section className="rounded-xl border border-border/60 bg-card/30 p-4">
					<h2 className="mb-3 text-sm font-semibold tracking-wide uppercase text-muted-foreground">
						Place limit
					</h2>
					<div className="mb-3 flex gap-2">
						<button
							type="button"
							className={`flex-1 rounded-lg py-2 text-sm font-medium ${
								side === "buy"
									? "bg-emerald-600 text-white"
									: "bg-muted text-muted-foreground"
							}`}
							onClick={() => setSide("buy")}
						>
							Buy MMK
						</button>
						<button
							type="button"
							className={`flex-1 rounded-lg py-2 text-sm font-medium ${
								side === "sell"
									? "bg-rose-600 text-white"
									: "bg-muted text-muted-foreground"
							}`}
							onClick={() => setSide("sell")}
						>
							Sell MMK
						</button>
					</div>
					<label className="mb-2 block text-xs text-muted-foreground">
						Price (USDM per MMK)
						<input
							className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm"
							value={price}
							onChange={(e) => setPrice(e.target.value)}
						/>
					</label>
					<label className="mb-3 block text-xs text-muted-foreground">
						Size (MMK)
						<input
							className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm"
							value={size}
							onChange={(e) => setSize(e.target.value)}
						/>
					</label>

					<div className="mb-3 space-y-1 rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-xs">
						{side === "buy" ? (
							<p>
								<span className="text-muted-foreground">Available </span>
								<span className="font-mono-amount font-semibold">
									{formatAmountPlain(balances.USDM ?? 0, 2)} USDM
								</span>
							</p>
						) : (
							<p>
								<span className="text-muted-foreground">Available </span>
								<span className="font-mono-amount font-semibold">
									{formatAmountPlain(balances.MMK ?? 0, 2)} MMK
								</span>
							</p>
						)}
						<p className="text-muted-foreground">
							Est.{" "}
							{side === "buy" ? "cost" : "proceeds"}:{" "}
							<span className="font-mono-amount text-foreground">
								{estNotional != null
									? `${formatAmountPlain(estNotional, 2)} USDM`
									: "—"}
							</span>
						</p>
						{st?.settlementMode ? (
							<p className="text-[11px] text-muted-foreground">
								Escrow on — ICRC-2 approve required
							</p>
						) : null}
					</div>

					<p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
						If your buy price is above a resting ask (or sell below a bid), the
						order fills immediately against the book.{" "}
						<Link to={docsHow} className="underline">
							Learn more
						</Link>
					</p>

					<RequireAuthAction
						labelWhenUnauthed="Connect to place"
						disabled={placeMut.isPending || !bookId}
						onAuthenticated={() => {
							void onPlace();
						}}
					>
						{placeMut.isPending ? "Submitting…" : `Place ${side} limit`}
					</RequireAuthAction>
				</section>
			</div>

			{/* Recent fills */}
			<section className="mx-4 mt-6 rounded-xl border border-border/60 bg-card/30 p-4">
				<h2 className="mb-1 text-sm font-semibold tracking-wide uppercase text-muted-foreground">
					Recent fills
				</h2>
				<p className="mb-3 text-xs text-muted-foreground">
					Matched trades (automatic when prices cross).
				</p>
				<ul className="space-y-1 font-mono text-xs">
					{(tradesQ.data?.trades ?? []).map((t) => (
						<li
							key={t.id}
							className="flex flex-wrap items-baseline gap-2 border-b border-border/30 py-1.5 last:border-0"
						>
							<span>
								#{t.id} @ {formatAmountPlain(t.price, 4)} USDM ·{" "}
								{formatAmountPlain(t.size, 4)} MMK
							</span>
							<span className="text-muted-foreground">
								{t.tsMs > 0 ? new Date(t.tsMs).toLocaleString() : "—"}
							</span>
						</li>
					))}
					{bookId && (tradesQ.data?.trades?.length ?? 0) === 0 && (
						<li className="text-muted-foreground">No fills yet</li>
					)}
				</ul>
			</section>

			{/* My orders */}
			<section className="mx-4 mt-6 rounded-xl border border-border/60 bg-card/30 p-4">
				<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
					<h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
						My orders
					</h2>
					<Link
						to={ROUTES.wallet}
						className="text-xs font-medium text-primary underline"
					>
						Wallet balances
					</Link>
				</div>
				{!identity && (
					<p className="text-sm text-muted-foreground">Connect to see open orders.</p>
				)}
				<ul className="space-y-2 text-sm">
					{(myOrdersQ.data ?? [])
						.filter((o) => o.status === "open")
						.map((o) => (
							<li
								key={o.id}
								className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/40 px-3 py-2"
							>
								<span className="font-mono text-xs">
									#{o.id} {o.side} @ {formatAmountPlain(o.price, 4)} USDM ×{" "}
									{formatAmountPlain(o.size, 4)} MMK
								</span>
								<button
									type="button"
									className="text-xs text-rose-400 underline"
									disabled={cancelMut.isPending}
									onClick={() => {
										cancelMut.mutate(o.id, {
											onSuccess: () => toast.success("Cancelled"),
											onError: (e) =>
												toast.error(
													e instanceof Error ? e.message : "Cancel failed",
												),
										});
									}}
								>
									Cancel
								</button>
							</li>
						))}
					{identity &&
						(myOrdersQ.data ?? []).filter((o) => o.status === "open").length ===
							0 && (
							<li className="text-muted-foreground">No open orders</li>
						)}
				</ul>
			</section>
		</div>
	);
}
